import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Bell, BellOff, CheckCheck, Loader2, MessageSquare } from 'lucide-react';
import { API } from '@/App';
import { toast } from 'sonner';
import Sidebar from '@/components/Sidebar';

export default function NotificationsPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    fetchUser();
    fetchNotifications();
  }, []);

  const fetchUser = async () => {
    const token = localStorage.getItem('token');
    if (!token) { navigate('/auth'); return; }
    try {
      const response = await fetch(`${API}/auth/me`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (response.ok) {
        setUser(await response.json());
      } else {
        navigate('/auth');
      }
    } catch (error) {
      navigate('/auth');
    }
  };

  const fetchNotifications = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API}/notifications`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (response.ok) {
        const data = await response.json();
        setNotifications(data.notifications);
        setUnreadCount(data.unread_count);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAllRead = async () => {
    const token = localStorage.getItem('token');
    try {
      await fetch(`${API}/notifications/mark-all-read`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('All notifications marked as read');
      fetchNotifications();
    } catch (error) {
      toast.error('Failed');
    }
  };

  const handleNotificationClick = async (notification) => {
    if (notification.link_url) navigate(notification.link_url);
    if (!notification.read) {
      const token = localStorage.getItem('token');
      await fetch(`${API}/notifications/${notification.notification_id}/read`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchNotifications();
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin" /></div>;

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden">
      <Sidebar user={user} />
      <div className="flex-1 flex flex-col lg:ml-64">
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-4xl mx-auto px-6 py-8">
            <div className="flex justify-between mb-8">
              <div>
                <h1 className="text-2xl font-bold">Notifications</h1>
                <p className="text-slate-500">{unreadCount > 0 ? `${unreadCount} unread` : 'All caught up!'}</p>
              </div>
              {unreadCount > 0 && (
                <Button onClick={handleMarkAllRead} variant="outline"><CheckCheck className="w-4 h-4 mr-2"/>Mark All Read</Button>
              )}
            </div>
            {notifications.length === 0 ? (
              <div className="bg-white rounded-2xl border p-12 text-center">
                <BellOff className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                <h3 className="text-lg font-semibold mb-2">No notifications</h3>
              </div>
            ) : (
              <div className="space-y-3">
                {notifications.map(notification => (
                  <div
                    key={notification.notification_id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`p-5 rounded-xl border-2 cursor-pointer ${notification.read ? 'bg-white border-slate-200' : 'bg-violet-50 border-violet-300'}`}
                  >
                    <div className="flex gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${notification.read ? 'bg-slate-100' : 'bg-violet-100'}`}>
                        <MessageSquare className={`w-5 h-5 ${notification.read ? 'text-slate-600' : 'text-violet-600'}`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between mb-1">
                          <h3 className="font-semibold">{notification.title}</h3>
                          {!notification.read && <span className="w-2 h-2 rounded-full bg-violet-600"></span>}
                        </div>
                        <p className="text-sm text-slate-600 mb-2">{notification.message}</p>
                        <p className="text-xs text-slate-500">{new Date(notification.created_at).toLocaleString()}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
